import { useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useI18n } from '../../contexts/I18nContext';
import { useThroughputReport } from '../../hooks/useTaskReports';
import type { ReportFilters, ThroughputBucket } from '../../lib/taskReportsApi';
import { FIRMIO_COLORS } from '../dashboard/charts';
import { ReportCard, SectionState } from './common';
import { CHART_TOOLTIP_STYLE } from '../../lib/chartTheme';

// Created vs. completed tasks over time, weekly/monthly buckets.

export function ThroughputSection({ filters }: { filters: ReportFilters }) {
  const { t } = useI18n();
  const [bucket, setBucket] = useState<ThroughputBucket>('week');
  const { data, isLoading, isError } = useThroughputReport(filters, bucket);

  const toggleCls = (active: boolean) =>
    `rounded-md px-2.5 py-1 text-xs font-medium ${
      active ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'
    }`;

  return (
    <ReportCard
      title={t('reports.throughput.title')}
      actions={
        <div className="flex gap-1">
          <button
            type="button"
            className={toggleCls(bucket === 'week')}
            onClick={() => setBucket('week')}
          >
            {t('reports.throughput.weekly')}
          </button>
          <button
            type="button"
            className={toggleCls(bucket === 'month')}
            onClick={() => setBucket('month')}
          >
            {t('reports.throughput.monthly')}
          </button>
        </div>
      }
    >
      <SectionState isLoading={isLoading} isError={isError} />
      {data && (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.buckets}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis
                dataKey="bucketStart"
                tick={{ fontSize: 12, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 12, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area
                type="monotone"
                dataKey="created"
                name={t('reports.throughput.created')}
                stroke={FIRMIO_COLORS.blue}
                fill={FIRMIO_COLORS.blue}
                fillOpacity={0.15}
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="completed"
                name={t('reports.throughput.completed')}
                stroke={FIRMIO_COLORS.green}
                fill={FIRMIO_COLORS.green}
                fillOpacity={0.15}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </ReportCard>
  );
}
